import { Box, Stack, Typography, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { RobotOutlined, ReloadOutlined, DisconnectOutlined } from '@ant-design/icons';

interface Props {
  error: string;
  code?: string;
  retryable?: boolean;
  canRetry: boolean;
  onRetry?: () => void;
}

const OFFLINE_CODES = new Set(['GATEWAY_UNREACHABLE', 'GATEWAY_HTTP', 'STREAM_FAILED']);

const AgentErrorCard = ({ error, code, retryable, canRetry, onRetry }: Props) => {
  const theme = useTheme();
  const isOffline = !!code && OFFLINE_CODES.has(code);
  const showRetry = (retryable ?? canRetry) && canRetry && !!onRetry;
  const accent = theme.palette.error.main;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" mb={1.25}>
        <Box
          sx={{
            width: 28, height: 28, borderRadius: 1.25, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.error.light}, ${theme.palette.error.dark})`,
            boxShadow: `0 2px 8px ${alpha(accent, 0.35)}`,
          }}
        >
          {isOffline
            ? <DisconnectOutlined style={{ fontSize: 14, color: '#fff' }} />
            : <RobotOutlined style={{ fontSize: 14, color: '#fff' }} />}
        </Box>
        <Typography sx={{
          color: accent, fontWeight: 700, fontSize: '0.78rem',
          textTransform: 'uppercase', letterSpacing: 1.2,
        }}>
          {isOffline ? 'AI Agent offline' : 'AI Assistant — Error'}
        </Typography>
        {code && (
          <Typography sx={{
            ml: 'auto', color: alpha(theme.palette.text.primary, 0.55),
            fontFamily: 'SF Mono, Menlo, Consolas, monospace',
            fontSize: '0.68rem', letterSpacing: 0.4,
          }}>
            {code}
          </Typography>
        )}
      </Stack>

      <Box sx={{ pl: 1.75, borderLeft: `2px solid ${alpha(accent, 0.3)}`, ml: 0.5 }}>
        <Box sx={{
          px: 1.75, py: 1.25, borderRadius: 1.5,
          bgcolor: alpha(accent, 0.08),
          border: `1px solid ${alpha(accent, 0.22)}`,
        }}>
          <Typography sx={{
            fontSize: '0.9375rem',
            color: alpha(theme.palette.text.primary, 0.9),
            lineHeight: 1.55,
            mb: showRetry ? 1.25 : 0,
          }}>
            {error || 'Unexpected AI agent error.'}
          </Typography>
          {showRetry && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReloadOutlined style={{ fontSize: 13 }} />}
              onClick={onRetry}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                py: 0.625,
                px: 1.5,
                borderColor: alpha(accent, 0.5),
                color: accent,
                '&:hover': {
                  borderColor: accent,
                  bgcolor: alpha(accent, 0.08),
                },
              }}
            >
              Retry
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AgentErrorCard;
